import TGASDK from '@/config/TGASDK'
import logger from '@/helper/logger'
import type { AuthContextType, AuthStatus } from '@/type/auth'
import { UC } from '@kulee/tga-sdk'
import type {
  LoginByAccountOptions,
  RegisterByAccountBindEmailOptions,
  UpdateUserInfoOptions,
  UserInfo
} from '@kulee/tga-sdk/types/types/uc'
import { useEffect, useMemo, useState } from 'react'

const useProvideAuth = (): AuthContextType => {
  const [status, setStatus] = useState<AuthStatus>('unready')
  const [user, setUser] = useState<UserInfo | null>(null)

  const getUserInfo = async (): Promise<UserInfo> => {
    const user = await TGASDK.module.UC.getUserInfo()
    setUser(user)

    return user
  }

  // 用户认证初始化
  const init = async () => {
    try {
      // 快速登录
      await TGASDK.module.UC.quickLogin()
      // 获取用户信息
      const user = await getUserInfo()

      setStatus('authorized')

      // 统计配置
      window.dataKule.push('config', {
        userId: user.id
      })
    } catch (error) {
      setStatus('unauthorized')
      logger.info('auth status: unauthorized')
    }
  }

  // 注册
  const register = async (options: RegisterByAccountBindEmailOptions) => {
    // 用户注册
    await TGASDK.module.UC.registerByAccountBindEmail(options)
    // 获取用户信息
    await getUserInfo()
    setStatus('authorized')
  }

  // 登录
  const login = async (options: LoginByAccountOptions) => {
    // 普通帐密登录
    await TGASDK.module.UC.loginByAccount(options)
    // 获取用户信息
    await getUserInfo()
    setStatus('authorized')
  }

  // 从SDK登录
  const loginFromSDK = async () => {
    // 从SDK登录
    await TGASDK.module.UC.loginFromSDK()
    // 获取用户信息
    await getUserInfo()

    setStatus('authorized')
  }

  // 登出
  const logout = () => {
    TGASDK.module.UC.logout()

    setStatus('unauthorized')
    setUser(null)
  }

  // 刷新用户信息
  const refreshUserInfo = async () => {
    const user = await TGASDK.module.UC.getUserInfoFromServer()
    setUser(user)
  }

  // 更新用户信息
  const updateUserInfo = async (options: UpdateUserInfoOptions) => {
    await TGASDK.module.UC.updateUserInfo(options)

    // 获取用户信息
    await refreshUserInfo()
  }

  useEffect(() => {
    TGASDK.addModule(UC)
  }, [])

  return useMemo(
    () => ({
      status,
      user,
      init,
      register,
      loginFromSDK,
      login,
      logout,
      updateUserInfo,
      refreshUserInfo
    }),
    [status, user]
  )
}

export default useProvideAuth
